import { useTranslation } from 'react-i18next';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { useRef, useState, useEffect } from 'react';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseUpload } from '@app/components/common/BaseUpload/BaseUpload';
import { BaseSwitch } from '@app/components/common/BaseSwitch/BaseSwitch';
import config from '@app/config';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { Form, Space } from 'antd';
import { getSingleVariantAction, updateVariantAction } from '@app/store/slices/userSlice'; // Import appropriate actions
import { useNavigate, useParams } from 'react-router-dom';

const formItemLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

export const ValidationFormVariantEdit: React.FC<any> = ({ }) => {
  const formRef = useRef<any>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const [fileId, setFileId] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [options, setOptions] = useState([{ quantity: '', unit: '' }]); // For dynamic inputs
  const {id}=useParams();
  const navigate=useNavigate();
  useEffect(() => {
    if (id) {
      fetchVariantDetails();
    }
  }, [id]);

  const fetchVariantDetails = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getSingleVariantAction(id)).unwrap(); // Fetch variant details
      if (response && response.data) {
        setFormData(response?.data || {}); // Prefill variant name
        setOptions(response.data?.values || []); // Prefill variant options
      }
      setLoading(false);
    } catch (error) {
      console.log('error',error)
      setLoading(false);
      notificationController.error({ message: 'Failed to load variant details' });
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    const payload = {
      ...values,
      values: options, // Include updated options
    };

    dispatch(updateVariantAction({ id: id, payload }))
      .unwrap()
      .then((resp: any) => {
        setLoading(false);
        notificationController.success({ message: t('common.success') });
        navigate('/admin/variants/list');
      })
      .catch((err: any) => {
        setLoading(false);
        notificationController.error({ message: err.message });
      });
  };

  const addOption = () => {
    setOptions([...options, { quantity: '', unit: '' }]);
  };

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const handleOptionChange = (index: number, key: string, value: string) => {
    const newOptions = [...options];
    newOptions[index][key] = value;
    setOptions(newOptions);
  };

  const beforeUpload = (file: any) => {
    const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJPG) {
      notificationController.warning({
        message: 'Only JPG/PNG files are allowed!',
      });
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5e6) {
      notificationController.warning({ message: 'Max file size is 5MB' });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const normFile = (e: any) => {
    if (e.file.status === 'done') {
      setFileId(e.file.response.file._id);
    }
    return e.fileList;
  };

  return (
    <>
      {!isLoading && <Form
        {...formItemLayout}
        ref={formRef}
        name="editVariantForm"
        initialValues={formData}
        onFinish={onFinish}
      >
        <BaseRow gutter={[10, 10]}>
          <BaseCol xs={24} sm={12}>
            <BaseButtonsForm.Item
              name="name"
              label="Variant Name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <BaseInput />
            </BaseButtonsForm.Item>
          </BaseCol>
        </BaseRow>

        <BaseRow>
          <BaseCol span={24}>
            <Form.Item label="Variant Options">
              {options.map((option, index) => (
                <Space key={index} align="baseline" style={{ marginTop: '15px', display: 'flex' }}>
                  <BaseInput
                    placeholder="Quantity"
                    value={option.quantity}
                    onChange={(e) => handleOptionChange(index, 'quantity', e.target.value)}
                  />
                  <BaseInput
                    placeholder="Unit (e.g., g, kg)"
                    value={option.unit}
                    onChange={(e) => handleOptionChange(index, 'unit', e.target.value)}
                  />
                  {options.length > 1 && (
                    <BaseButton type="default" danger onClick={() => removeOption(index)}>
                      Remove
                    </BaseButton>
                  )}
                </Space>
              ))}
              <BaseButton
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addOption}
                style={{ marginTop: '10px' }}
              >
                Add Option
              </BaseButton>
            </Form.Item>
          </BaseCol>
        </BaseRow>



        <BaseRow gutter={[10, 10]}>
          <BaseCol xs={24} sm={12} md={12} xl={6}>
            <BaseButtonsForm.Item>
              <BaseButton type="primary" htmlType="submit" loading={isLoading}>
                Save Changes
              </BaseButton>
            </BaseButtonsForm.Item>
          </BaseCol>
        </BaseRow>
      </Form>
}
    </>
  );
};
